import React, { useState } from 'react'
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  VStack,
  HStack,
  useDisclosure,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { MdRefresh, MdGavel, MdPlayArrow, MdSecurity, MdReceipt, MdWarning } from 'react-icons/md'
import {
  useGetConflicts,
  useRunReconciliation,
  useGetAuditLogs,
} from 'queries/reconciliation.query'
import { ResolveConflictModal } from './ResolveConflictModal'

export default function ReconciliationView() {
  const [conflictPage, setConflictPage] = useState(1)
  const [auditPage, setAuditPage] = useState(1)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [outputModalContent, setOutputModalContent] = useState(null)

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700')

  const toast = useToast()
  const { isOpen: isResolveOpen, onOpen: onOpenResolve, onClose: onCloseResolve } = useDisclosure()
  const { isOpen: isOutputOpen, onOpen: onOpenOutput, onClose: onCloseOutput } = useDisclosure()

  // Queries
  const {
    data: conflictsRes,
    isLoading: isConflictsLoading,
    refetch: refetchConflicts,
    isFetching: isConflictsFetching,
  } = useGetConflicts(conflictPage)

  const {
    data: auditRes,
    isLoading: isAuditLoading,
    refetch: refetchAudit,
    isFetching: isAuditFetching,
  } = useGetAuditLogs(auditPage)

  const runReconciliationMutation = useRunReconciliation()

  const conflicts = conflictsRes?.data?.data || []
  const conflictsPagination = conflictsRes?.data || {}

  const auditLogs = auditRes?.data?.data || []
  const auditPagination = auditRes?.data || {}

  const handleOpenResolve = (order) => {
    setSelectedOrder(order)
    onOpenResolve()
  }

  const handleRunReconciliation = async () => {
    try {
      const res = await runReconciliationMutation.mutateAsync()
      setOutputModalContent(res?.output || res?.message || 'Routine finished.')
      onOpenOutput()
      toast({
        title: 'Reconciliation Executed',
        description: res?.message || 'Expired holds and reconciliation routine completed.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
    } catch (err) {
      toast({
        title: 'Reconciliation Failed',
        description: err?.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      {/* Top Header */}
      <Flex px="25px" justify="space-between" mb="20px" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align="flex-start" spacing={1}>
          <HStack spacing={2.5}>
            <Icon as={MdSecurity} w="24px" height="24px" color="purple.500" />
            <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
              Financial Reconciliation & Audit Trail
            </Text>
          </HStack>
          <Text color={textColorSecondary} fontSize="sm">
            Monitor inventory lock exceptions, resolve late payment conflicts, and review immutable financial audit logs.
          </Text>
        </VStack>

        <HStack spacing={3}>
          <Button
            size="sm"
            colorScheme="purple"
            borderRadius="10px"
            leftIcon={<MdPlayArrow />}
            onClick={handleRunReconciliation}
            isLoading={runReconciliationMutation.isPending}
            loadingText="Reconciling..."
          >
            Run Reconciliation Routine
          </Button>

          <IconButton
            size="sm"
            aria-label="Refresh"
            icon={<MdRefresh />}
            borderRadius="10px"
            isLoading={isConflictsFetching || isAuditFetching}
            onClick={() => {
              refetchConflicts()
              refetchAudit()
            }}
          />
        </HStack>
      </Flex>

      {/* Tabs */}
      <Tabs colorScheme="purple" isLazy px="25px">
        <TabList borderColor={borderColor} mb={4}>
          <Tab fontWeight="bold" fontSize="sm">
            <HStack spacing={2}>
              <Icon as={MdWarning} color={conflicts.length > 0 ? 'orange.500' : 'gray.400'} />
              <Text>Inventory & Payment Conflicts</Text>
              {conflicts.length > 0 && (
                <Badge colorScheme="red" borderRadius="full" px={2}>
                  {conflicts.length}
                </Badge>
              )}
            </HStack>
          </Tab>
          <Tab fontWeight="bold" fontSize="sm">
            <HStack spacing={2}>
              <Icon as={MdReceipt} />
              <Text>Audit Logs Trail</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Panel 1: Conflicts */}
          <TabPanel px={0}>
            {isConflictsLoading ? (
              <SkeletonTable />
            ) : conflicts.length === 0 ? (
              <Flex justify="center" align="center" direction="column" py="50px">
                <Icon as={MdSecurity} w="48px" height="48px" color="green.400" mb={3} />
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  All Systems Reconciled
                </Text>
                <Text fontSize="xs" color={textColorSecondary} mt={1}>
                  No inventory or payment discrepancies currently require manual administrative intervention.
                </Text>
              </Flex>
            ) : (
              <Table variant="simple" color="gray.500" mb="24px">
                <Thead bg={TheadBackground}>
                  <Tr>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">ORDER / REF</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">CUSTOMER</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">TOTAL AMOUNT</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">GATEWAY & ATTEMPTS</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">CONFLICT STATUS</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px" textAlign="right">RESOLUTION</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {conflicts.map((order) => (
                    <Tr key={order.id} _hover={{ bg: hoverBackgroundColor }}>
                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" fontWeight="bold" fontFamily="mono" color={textColor}>
                          {order.order_number}
                        </Text>
                        <Text fontSize="10px" color={textColorSecondary}>
                          {order.created_at ? new Date(order.created_at).toLocaleString() : '—'}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" fontWeight="semibold" color={textColor}>
                          {order.customer_name}
                        </Text>
                        <Text fontSize="10px" color={textColorSecondary}>
                          {order.customer_email}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" fontWeight="bold" color="brand.500">
                          €{Number(order.total_amount).toFixed(2)}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Badge colorScheme="purple" fontSize="10px" textTransform="uppercase">
                          {order.payment_gateway || 'Stripe/PayPal'}
                        </Badge>
                        <Text fontSize="10px" color={textColorSecondary} mt={0.5}>
                          {order.payment_attempts?.length || 0} attempts
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Badge colorScheme="red" px={2} py={0.5} borderRadius="full">
                          {order.payment_status}
                        </Badge>
                      </Td>

                      <Td borderColor={borderColor} textAlign="right">
                        <Button
                          size="xs"
                          colorScheme="purple"
                          leftIcon={<MdGavel />}
                          borderRadius="8px"
                          onClick={() => handleOpenResolve(order)}
                        >
                          Resolve Conflict
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}

            {conflictsPagination.total > 20 && (
              <Flex px="10px" py="12px" justify="space-between" align="center" borderTop="1px solid" borderColor={borderColor}>
                <Text fontSize="xs" color={textColorSecondary}>
                  Showing {conflictsPagination.from || 0} to {conflictsPagination.to || 0} of {conflictsPagination.total || 0}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    isDisabled={conflictPage <= 1}
                    onClick={() => setConflictPage((p) => Math.max(1, p - 1))}
                    borderRadius="8px"
                  >
                    Previous
                  </Button>
                  <Text fontSize="xs" fontWeight="bold">Page {conflictPage}</Text>
                  <Button
                    size="xs"
                    isDisabled={conflictPage >= (conflictsPagination.last_page || 1)}
                    onClick={() => setConflictPage((p) => p + 1)}
                    borderRadius="8px"
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </TabPanel>

          {/* Panel 2: Audit Logs */}
          <TabPanel px={0}>
            {isAuditLoading ? (
              <SkeletonTable />
            ) : auditLogs.length === 0 ? (
              <Flex justify="center" align="center" direction="column" py="50px">
                <Text fontSize="sm" color={textColorSecondary}>
                  No audit logs recorded yet.
                </Text>
              </Flex>
            ) : (
              <Table variant="simple" color="gray.500" mb="24px">
                <Thead bg={TheadBackground}>
                  <Tr>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">ACTION</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">ENTITY</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">USER / IP</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">JUSTIFICATION / REASON</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">DETAILS</Th>
                    <Th borderColor={borderColor} color="gray.400" fontSize="11px">TIMESTAMP</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {auditLogs.map((log) => (
                    <Tr key={log.id} _hover={{ bg: hoverBackgroundColor }}>
                      <Td borderColor={borderColor}>
                        <Badge
                          colorScheme={
                            log.action?.includes('approved')
                              ? 'green'
                              : log.action?.includes('rejected')
                              ? 'red'
                              : 'blue'
                          }
                          fontSize="10px"
                          px={2}
                          py={0.5}
                          borderRadius="6px"
                        >
                          {log.action}
                        </Badge>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" fontWeight="semibold">
                          {log.entity_type} #{log.entity_id}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        <Text fontSize="xs" color={textColor}>
                          {log.user?.name || 'System / Customer'}
                        </Text>
                        <Text fontSize="10px" color={textColorSecondary} fontFamily="mono">
                          {log.ip_address || '—'}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor} maxW="250px">
                        <Text fontSize="xs" noOfLines={2} title={log.reason}>
                          {log.reason || '—'}
                        </Text>
                      </Td>

                      <Td borderColor={borderColor}>
                        {log.new_values && (
                          <Tooltip label={JSON.stringify(log.new_values, null, 2)}>
                            <Code fontSize="10px" borderRadius="6px" p={1} maxW="200px" isTruncated display="block">
                              {JSON.stringify(log.new_values)}
                            </Code>
                          </Tooltip>
                        )}
                      </Td>

                      <Td borderColor={borderColor} fontSize="xs">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}

            {auditPagination.total > 25 && (
              <Flex px="10px" py="12px" justify="space-between" align="center" borderTop="1px solid" borderColor={borderColor}>
                <Text fontSize="xs" color={textColorSecondary}>
                  Showing {auditPagination.from || 0} to {auditPagination.to || 0} of {auditPagination.total || 0}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    isDisabled={auditPage <= 1}
                    onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                    borderRadius="8px"
                  >
                    Previous
                  </Button>
                  <Text fontSize="xs" fontWeight="bold">Page {auditPage}</Text>
                  <Button
                    size="xs"
                    isDisabled={auditPage >= (auditPagination.last_page || 1)}
                    onClick={() => setAuditPage((p) => p + 1)}
                    borderRadius="8px"
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Resolve Conflict Modal */}
      <ResolveConflictModal
        isOpen={isResolveOpen}
        onClose={onCloseResolve}
        order={selectedOrder}
      />

      {/* Routine Output Modal */}
      <Modal isOpen={isOutputOpen} onClose={onCloseOutput} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="18px">
          <ModalHeader fontSize="md" fontWeight="bold">
            Reconciliation Execution Summary
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Code p={4} borderRadius="12px" w="100%" fontSize="xs" whiteSpace="pre-wrap" display="block">
              {outputModalContent}
            </Code>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  )
}
